import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiBarChart, FiEdit2, FiEye, FiEyeOff, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import SEO from '../SEO/SEO';
import { adminAPI } from '../../services/blogApi';
import { BlogListItem } from '../../types';

const PAGE_SIZE = 10;

const AdminDashboard: React.FC = () => {
  const [blogs, setBlogs] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadBlogs = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminAPI.listBlogs(page, PAGE_SIZE, statusFilter || undefined, search || undefined);
      setBlogs(data.data);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search, page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await adminAPI.deleteBlog(id);
      if (blogs.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
      } else {
        loadBlogs();
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const toggleStatus = async (blog: BlogListItem) => {
    const nextStatus = blog.status === 'published' ? 'draft' : 'published';
    try {
      const updated = await adminAPI.updateStatus(blog.id, nextStatus);
      setBlogs((prev) => prev.map((item) => (item.id === blog.id ? { ...item, ...updated } : item)));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Status update failed');
    }
  };

  return (
    <>
      <SEO title="Admin Dashboard" description="Blog admin dashboard" url="/admin/dashboard" />
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Blog Dashboard</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Manage articles, track views, and publish SEO content
            </p>
          </div>
          <Link
            to="/admin/blogs/new"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 shrink-0"
          >
            <FiPlus /> New Article
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-grow">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Search articles..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800"
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>

        {loading && <p className="text-gray-500">Loading blogs...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && blogs.length === 0 && (
          <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400 mb-4">No blog articles found.</p>
            <Link to="/admin/blogs/new" className="text-blue-600 font-semibold">
              Create your first article
            </Link>
          </div>
        )}

        <div className="space-y-4">
          {blogs.map((blog) => (
            <div
              key={blog.id}
              className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            >
              <div className="min-w-0 flex-grow">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">{blog.title}</h2>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      blog.status === 'published'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                    }`}
                  >
                    {blog.status}
                  </span>
                  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                    {blog.category || 'General'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{blog.excerpt}</p>
                <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-400">
                  <span>Keyword: {blog.primary_keyword}</span>
                  <span className="inline-flex items-center gap-1">
                    <FiBarChart size={12} /> {blog.views ?? 0} views
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 shrink-0">
                {blog.status === 'published' && (
                  <Link
                    to={`/blog/${blog.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
                  >
                    View
                  </Link>
                )}
                <Link
                  to={`/admin/blogs/edit/${blog.id}`}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm"
                >
                  <FiEdit2 /> Edit
                </Link>
                <button
                  type="button"
                  onClick={() => toggleStatus(blog)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm"
                >
                  {blog.status === 'published' ? (
                    <>
                      <FiEyeOff /> Unpublish
                    </>
                  ) : (
                    <>
                      <FiEye /> Publish
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(blog.id, blog.title)}
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-sm"
                >
                  <FiTrash2 /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-8">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} ({total} articles)
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((prev) => prev - 1)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((prev) => prev + 1)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminDashboard;
